import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import CustomAlert from "../../../components/Alerts/CustomAlert";
import { addAlert, removeAlert } from "../../../Utils/helper";
import styles from "./ApiKeys.module.css";
import Button from "../../../components/Button/Button";
import Modal from "../../../components/Dialog/Modal";
import LoadingOverlay from "../../../components/Loading/LoadingOverlay";
import { open_api_with_cookies, base_url } from "../../../Utils/network";
import { Clipboard, Copy } from "lucide-react";

const formatEndpointLines = (rows) =>
  rows.map((row) => `${row.method} ${row.path} - ${row.scope}`).join("\n");

const EndpointTable = ({ rows }) => (
  <div className={styles.tableWrapper}>
    <table className={styles.docTable}>
      <thead>
        <tr>
          <th>Method</th>
          <th>Path</th>
          <th>Scope required</th>
          <th>When to use</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={`${row.method}-${row.path}`}>
            <td>{row.method}</td>
            <td>
              <code>{row.path}</code>
            </td>
            <td>
              <code>{row.scope}</code>
            </td>
            <td>{row.use}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CopyButton = ({ label = "Copy", onClick, variant = "code" }) => (
  <button
    className={variant === "section" ? styles.copySectionBtn : styles.copyBtn}
    onClick={onClick}
    type="button"
  >
    {variant === "section" ? <Clipboard size={14} /> : <Copy size={14} />}
    <span>{label}</span>
  </button>
);

const CodeBlock = ({ value, label, onCopy }) => (
  <div className={styles.codeBlockWrap}>
    <CopyButton label={label} onClick={() => onCopy(value)} />
    <pre className={styles.codeBlock}>{value}</pre>
  </div>
);

const DocSection = ({ id, eyebrow, title, description, copyText, onCopy, children }) => (
  <section className={styles.docPanel} id={id}>
    <div className={styles.docSectionHeader}>
      <div>
        <span className={styles.docEyebrow}>{eyebrow}</span>
        <h4 className={styles.docHeading}>{title}</h4>
        <p className={styles.description}>{description}</p>
      </div>
      <CopyButton
        label="Copy section"
        onClick={() => onCopy(copyText, `${title} copied`)}
        variant="section"
      />
    </div>
    {children}
  </section>
);

const ApiKeys = () => {
  const isAdmin = Cookies.get("isAdmin") === "true";

  const [alerts, setAlerts] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [keys, setKeys] = useState([]);
  const [scopeCatalogue, setScopeCatalogue] = useState({}); // { "blog:read": "Read blog posts", ... }

  // Create-key modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState({
    name: "",
    scopes: [],
    expires_at: "",
    throttle_rate: "",
  });

  // One-time secret reveal
  const [newSecret, setNewSecret] = useState(null);

  const handleAddAlert = (title, message, type, autoClose = true) => {
    addAlert(title, message, type, autoClose, setAlerts);
    setShowAlert(true);
  };

  const handleRemoveAlert = (id) => {
    removeAlert(id, setAlerts);
    if (alerts.length <= 1) setShowAlert(false);
  };

  useEffect(() => {
    if (isAdmin) {
      fetchKeys();
      fetchScopes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchKeys = async () => {
    setIsLoading(true);
    try {
      const res = await open_api_with_cookies.get("/api-keys/keys/");
      // DRF PageNumberPagination -> { results: [...] }; fall back to a raw array.
      const data = res.data?.results ?? res.data ?? [];
      setKeys(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching API keys:", error);
      handleAddAlert("Error", "Failed to load API keys.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchScopes = async () => {
    try {
      const res = await open_api_with_cookies.get("/api-keys/keys/scopes/");
      setScopeCatalogue(res.data?.scopes ?? {});
    } catch (error) {
      console.error("Error fetching scopes:", error);
    }
  };

  const toggleScope = (scope) => {
    setForm((prev) => {
      const has = prev.scopes.includes(scope);
      return {
        ...prev,
        scopes: has
          ? prev.scopes.filter((s) => s !== scope)
          : [...prev.scopes, scope],
      };
    });
  };

  const resetForm = () => {
    setForm({ name: "", scopes: [], expires_at: "", throttle_rate: "" });
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const createKey = async () => {
    if (!form.name.trim()) {
      handleAddAlert("Missing name", "Please give the key a name.", "warning");
      return;
    }
    if (form.scopes.length === 0) {
      handleAddAlert(
        "No scopes selected",
        "Select at least one permission for this key.",
        "warning"
      );
      return;
    }
    const userId = Cookies.get("userId");
    if (!userId) {
      handleAddAlert(
        "Error",
        "Could not determine your user id. Please re-login and try again.",
        "error"
      );
      return;
    }

    const payload = {
      name: form.name.trim(),
      user: Number(userId),
      scopes: form.scopes,
    };
    if (form.expires_at) {
      // Native date input gives YYYY-MM-DD; expire at end of that day.
      payload.expires_at = new Date(`${form.expires_at}T23:59:59`).toISOString();
    }
    if (form.throttle_rate.trim()) {
      payload.throttle_rate = form.throttle_rate.trim();
    }

    setIsLoading(true);
    try {
      const res = await open_api_with_cookies.post("/api-keys/keys/", payload);
      if ([200, 201].includes(res.status)) {
        setShowCreateModal(false);
        setNewSecret({ name: res.data?.name, key: res.data?.key });
        handleAddAlert("Success", "API key created.", "success");
        fetchKeys();
      }
    } catch (error) {
      console.error("Error creating API key:", error);
      const msg =
        error?.response?.data?.scopes?.[0] ||
        error?.response?.data?.detail ||
        "Failed to create API key.";
      handleAddAlert("Failed", msg, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const revokeKey = async (key) => {
    if (
      !window.confirm(
        `Revoke "${key.name}" (${key.prefix})? Any integration using it will stop working immediately.`
      )
    )
      return;
    setIsLoading(true);
    try {
      const res = await open_api_with_cookies.post(
        `/api-keys/keys/${key.id}/revoke/`
      );
      if ([200, 201].includes(res.status)) {
        handleAddAlert("Revoked", `"${key.name}" has been revoked.`, "success");
        fetchKeys();
      }
    } catch (error) {
      console.error("Error revoking API key:", error);
      handleAddAlert("Failed", "Failed to revoke the key.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const rotateKey = async (key) => {
    if (
      !window.confirm(
        `Rotate "${key.name}"? This revokes the current key and issues a new secret with the same settings.`
      )
    )
      return;
    setIsLoading(true);
    try {
      const res = await open_api_with_cookies.post(
        `/api-keys/keys/${key.id}/rotate/`
      );
      if ([200, 201].includes(res.status)) {
        setNewSecret({ name: res.data?.name, key: res.data?.key });
        handleAddAlert("Rotated", "A new secret has been issued.", "success");
        fetchKeys();
      }
    } catch (error) {
      console.error("Error rotating API key:", error);
      handleAddAlert("Failed", "Failed to rotate the key.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteKey = async (key) => {
    if (
      !window.confirm(
        `Permanently delete "${key.name}" (${key.prefix})? This cannot be undone.`
      )
    )
      return;
    setIsLoading(true);
    try {
      const res = await open_api_with_cookies.delete(
        `/api-keys/keys/${key.id}/`
      );
      if ([200, 204].includes(res.status)) {
        handleAddAlert("Deleted", `"${key.name}" has been deleted.`, "success");
        fetchKeys();
      }
    } catch (error) {
      console.error("Error deleting API key:", error);
      handleAddAlert("Failed", "Failed to delete the key.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text, label = "Copied") => {
    navigator.clipboard
      .writeText(text)
      .then(() => handleAddAlert(label, "Copied to clipboard.", "success"))
      .catch(() =>
        handleAddAlert("Error", "Could not copy to clipboard.", "error")
      );
  };

  const statusOf = (key) => {
    if (key.is_revoked) return { label: "Revoked", cls: styles.badgeRevoked };
    if (key.is_expired) return { label: "Expired", cls: styles.badgeExpired };
    if (!key.is_active) return { label: "Inactive", cls: styles.badgeInactive };
    return { label: "Active", cls: styles.badgeActive };
  };

  const formatDate = (value) => {
    if (!value) return "—";
    try {
      return new Date(value).toLocaleString();
    } catch (e) {
      return value;
    }
  };

  if (!isAdmin) {
    return (
      <div className={styles.mainContainer}>
        <div className={styles.settingsPage}>
          <div className={styles.card}>
            <h4>Access restricted</h4>
            <p className={styles.description}>
              API key management is available to administrators only.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const blogEndpoints = [
    {
      method: "GET",
      path: "/blog/api/v1/posts/",
      scope: "blog:read",
      use: "List published posts for sync or audit tools.",
    },
    {
      method: "GET",
      path: "/blog/api/v1/posts/{id-or-slug}/",
      scope: "blog:read",
      use: "Fetch one post before updating or republishing.",
    },
    {
      method: "POST",
      path: "/blog/api/v1/posts/",
      scope: "blog:write",
      use: "Create an article after uploading referenced images.",
    },
    {
      method: "PATCH",
      path: "/blog/api/v1/posts/{id-or-slug}/",
      scope: "blog:write",
      use: "Update selected fields without replacing the whole post.",
    },
    {
      method: "DELETE",
      path: "/blog/api/v1/posts/{id-or-slug}/",
      scope: "blog:delete",
      use: "Remove a post through an approved integration.",
    },
    {
      method: "POST",
      path: "/blog/api/v1/images/",
      scope: "blog:write",
      use: "Upload a cover or body image and use the returned id.",
    },
  ];

  const recipeEndpoints = [
    {
      method: "GET",
      path: "/recipe/api/v1/recipes/",
      scope: "recipe:read",
      use: "List recipes and verify ids before updates.",
    },
    {
      method: "POST",
      path: "/recipe/api/v1/recipes/",
      scope: "recipe:write",
      use: "Create recipes, optionally passing user_id for uploader attribution.",
    },
    {
      method: "PATCH",
      path: "/recipe/api/v1/recipes/{id-or-slug}/",
      scope: "recipe:write",
      use: "Update recipe fields or replace relation lists.",
    },
    {
      method: "DELETE",
      path: "/recipe/api/v1/recipes/{id-or-slug}/",
      scope: "recipe:delete",
      use: "Delete recipes from trusted back-office tools.",
    },
    {
      method: "POST",
      path: "/recipe/api/v1/images/",
      scope: "recipe:write",
      use: "Upload banners, category icons, audience icons, or ingredient images by type.",
    },
    {
      method: "GET / POST / PUT / PATCH",
      path: "/recipe/api/v1/categories/",
      scope: "recipe:read / recipe:write",
      use: "List, create, or update category_ids. Create requires an icon.",
    },
    {
      method: "GET / POST / PUT / PATCH",
      path: "/recipe/api/v1/audiences/",
      scope: "recipe:read / recipe:write",
      use: "List, create, or update intended_audience_ids. Create requires an icon.",
    },
    {
      method: "GET / POST / PUT / PATCH",
      path: "/recipe/api/v1/ingredients/",
      scope: "recipe:read / recipe:write",
      use: "List, create, or update ingredient master rows referenced by ingredients_id.",
    },
    {
      method: "GET / POST / PUT / PATCH",
      path: "/recipe/api/v1/recipe-ingredients/",
      scope: "recipe:read / recipe:write",
      use: "Manage child ingredient rows for a recipe.",
    },
    {
      method: "GET / POST / PUT / PATCH",
      path: "/recipe/api/v1/directions/",
      scope: "recipe:read / recipe:write",
      use: "Manage standalone recipe direction rows.",
    },
    {
      method: "GET / POST / PUT / PATCH",
      path: "/recipe/api/v1/nutritions/",
      scope: "recipe:read / recipe:write",
      use: "Manage standalone nutrition rows.",
    },
    {
      method: "GET / POST / PUT / PATCH",
      path: "/recipe/api/v1/views/",
      scope: "recipe:read / recipe:write",
      use: "Manage recipe view tracking rows.",
    },
    {
      method: "GET / POST / PUT / PATCH",
      path: "/recipe/api/v1/favourites/",
      scope: "recipe:read / recipe:write",
      use: "Manage recipe favourite rows.",
    },
    {
      method: "GET / POST / PUT / PATCH",
      path: "/recipe/api/v1/likes/",
      scope: "recipe:read / recipe:write",
      use: "Manage recipe like rows.",
    },
    {
      method: "GET / POST / PUT / PATCH",
      path: "/recipe/api/v1/filter-chips/",
      scope: "recipe:read / recipe:write",
      use: "Manage recipe filter chip definitions.",
    },
  ];

  const recipeAdminEndpoints = [
    {
      method: "GET / POST / PUT / PATCH / DELETE",
      path: "/recipe/api/v1/admin/recipes/",
      scope: "recipe-admin:read / write / delete",
      use: "Manage the complete recipe record, nested content, media, publishing, and tags.",
    },
    {
      method: "GET / POST / PUT / PATCH / DELETE",
      path: "/recipe/api/v1/admin/recipe-categories/",
      scope: "recipe-admin:read / write / delete",
      use: "Manage category hierarchy, icons, display order, and copy.",
    },
    {
      method: "GET / POST / PUT / PATCH / DELETE",
      path: "/recipe/api/v1/admin/ingredient-master/",
      scope: "recipe-admin:read / write / delete",
      use: "Manage reusable ingredients, images, aliases, and allergen tags.",
    },
    {
      method: "GET / POST / PUT / PATCH / DELETE",
      path: "/recipe/api/v1/admin/recipe-ingredients/",
      scope: "recipe-admin:read / write / delete",
      use: "Manage quantities, units, positions, preparation notes, and scaling.",
    },
    {
      method: "GET / POST / PUT / PATCH / DELETE",
      path: "/recipe/api/v1/admin/directions/",
      scope: "recipe-admin:read / write / delete",
      use: "Manage ordered cooking steps, rich text, timers, and labels.",
    },
    {
      method: "GET / POST / PUT / PATCH / DELETE",
      path: "/recipe/api/v1/admin/nutrition/",
      scope: "recipe-admin:read / write / delete",
      use: "Manage legacy and structured per-serving nutrition values.",
    },
    {
      method: "GET / POST / PUT / PATCH / DELETE",
      path: "/recipe/api/v1/admin/filter-chips/",
      scope: "recipe-admin:read / write / delete",
      use: "Manage discovery filter definitions and visibility.",
    },
    {
      method: "GET / POST / PUT / PATCH / DELETE",
      path: "/recipe/api/v1/admin/views/",
      scope: "recipe-admin:read / write / delete",
      use: "Manage user-linked recipe view counters.",
    },
    {
      method: "GET / POST / PUT / PATCH / DELETE",
      path: "/recipe/api/v1/admin/favourites/",
      scope: "recipe-admin:read / write / delete",
      use: "Manage user-linked saved recipes.",
    },
    {
      method: "GET / POST / PUT / PATCH / DELETE",
      path: "/recipe/api/v1/admin/likes/",
      scope: "recipe-admin:read / write / delete",
      use: "Manage user-linked recipe likes.",
    },
    {
      method: "GET / POST / PUT / PATCH / DELETE",
      path: "/recipe/api/v1/admin/tags/",
      scope: "recipe-admin:read / write / delete",
      use: "Manage feature, goal, allergen, and dietary tags.",
    },
    {
      method: "GET / POST / PUT / PATCH / DELETE",
      path: "/recipe/api/v1/admin/collections/",
      scope: "recipe-admin:read / write / delete",
      use: "Manage curated recipe groups and presentation data.",
    },
    {
      method: "GET / POST / PUT / PATCH / DELETE",
      path: "/recipe/api/v1/admin/collection-items/",
      scope: "recipe-admin:read / write / delete",
      use: "Manage ordered recipe membership inside collections.",
    },
    {
      method: "GET / POST / PUT / PATCH / DELETE",
      path: "/recipe/api/v1/admin/home-sections/",
      scope: "recipe-admin:read / write / delete",
      use: "Manage home layouts, scheduling, eligibility, order, and enabled state.",
    },
    {
      method: "GET / POST / PUT / PATCH / DELETE",
      path: "/recipe/api/v1/admin/home-section-items/",
      scope: "recipe-admin:read / write / delete",
      use: "Manage section targets, campaign copy, CTA details, and imagery.",
    },
    {
      method: "GET / POST / PUT / PATCH / DELETE",
      path: "/recipe/api/v1/admin/interactions/",
      scope: "recipe-admin:read / write / delete",
      use: "Manage impression, open, save, dismiss, and cooking events.",
    },
    {
      method: "GET / POST / PUT / PATCH / DELETE",
      path: "/recipe/api/v1/admin/cook-sessions/",
      scope: "recipe-admin:read / write / delete",
      use: "Manage guided-cooking progress and completion state.",
    },
    {
      method: "GET / POST / PUT / PATCH / DELETE",
      path: "/recipe/api/v1/admin/meal-plans/",
      scope: "recipe-admin:read / write / delete",
      use: "Manage user and child meal-plan state and generation metadata.",
    },
    {
      method: "GET / POST / PUT / PATCH / DELETE",
      path: "/recipe/api/v1/admin/meal-plan-items/",
      scope: "recipe-admin:read / write / delete",
      use: "Manage day and meal-slot assignments, swaps, ordering, and rationale.",
    },
    {
      method: "GET / POST / PUT / PATCH / DELETE",
      path: "/recipe/api/v1/admin/experience-configurations/",
      scope: "recipe-admin:read / write / delete",
      use: "Manage versioned mobile recipe-experience settings.",
    },
    {
      method: "GET / POST / PUT / PATCH / DELETE",
      path: "/recipe/api/v1/admin/meal-plan-ai-configurations/",
      scope: "recipe-admin:read / write / delete",
      use: "Manage meal-plan models, prompts, limits, rollout, and activation.",
    },
    {
      method: "GET",
      path: "/recipe/api/v1/admin/recipe-types/",
      scope: "recipe-admin:read",
      use: "List valid recipe type ids for complete recipe payloads.",
    },
    {
      method: "GET",
      path: "/recipe/api/v1/admin/recipe-audiences/",
      scope: "recipe-admin:read",
      use: "List recipe audience ids and labels.",
    },
    {
      method: "GET",
      path: "/recipe/api/v1/admin/recipe-experts/",
      scope: "recipe-admin:read",
      use: "List expert ids that can be assigned as chef_id.",
    },
  ];

  const worksheetEndpoints = [
    {
      method: "GET",
      path: "/core/worksheet/api/v1/worksheets/",
      scope: "worksheet:read",
      use: "List uploaded or AI-generated worksheets.",
    },
    {
      method: "POST",
      path: "/core/worksheet/api/v1/worksheets/",
      scope: "worksheet:write",
      use: "Create a worksheet from an existing uploaded file path.",
    },
    {
      method: "PATCH",
      path: "/core/worksheet/api/v1/worksheets/{id}/",
      scope: "worksheet:write",
      use: "Update metadata or replace category/audience relation ids.",
    },
    {
      method: "DELETE",
      path: "/core/worksheet/api/v1/worksheets/{id}/",
      scope: "worksheet:delete",
      use: "Remove a worksheet from trusted admin tooling.",
    },
    {
      method: "POST",
      path: "/core/worksheet/api/v1/files/",
      scope: "worksheet:write",
      use: "Upload files, thumbnails, category icons, or audience icons by type.",
    },
    {
      method: "GET / POST",
      path: "/core/worksheet/api/v1/categories/",
      scope: "worksheet:read / worksheet:write",
      use: "List or create category_ids. Create requires an icon.",
    },
    {
      method: "GET / POST",
      path: "/core/worksheet/api/v1/audiences/",
      scope: "worksheet:read / worksheet:write",
      use: "List or create audience_ids. Create requires an icon.",
    },
    {
      method: "POST",
      path: "/core/worksheet/api/v1/generations/",
      scope: "worksheet:write or app auth",
      use: "Generate a new worksheet image with admin-controlled GPT image settings.",
    },
  ];

  const communityEndpoints = [
    {
      method: "GET / POST",
      path: "/community/api/v1/questions/",
      scope: "community:read / community:write",
      use: "Read or post community questions.",
    },
    {
      method: "PATCH / DELETE",
      path: "/community/api/v1/questions/{id}/",
      scope: "community:write / community:delete",
      use: "Moderate or update a question.",
    },
    {
      method: "GET / POST",
      path: "/community/api/v1/answers/",
      scope: "community:read / community:write",
      use: "Read or post replies to a question.",
    },
    {
      method: "PATCH / DELETE",
      path: "/community/api/v1/answers/{id}/",
      scope: "community:write / community:delete",
      use: "Moderate or update a reply.",
    },
  ];

  const curlList = `curl -H "X-API-Key: YOUR_KEY" \\\n  ${base_url}blog/api/v1/posts/`;

  const curlImage = `curl -X POST ${base_url}blog/api/v1/images/ \\
  -H "X-API-Key: YOUR_KEY" \\
  -F "file=@/path/to/cover.jpg" \\
  -F "title=Cover image"
# -> { "id": 42, "url": "https://.../cover.jpg", "width": 1600, "height": 900 }`;

  const curlCreate = `curl -X POST ${base_url}blog/api/v1/posts/ \\
  -H "X-API-Key: YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Hello World",
    "intro": "A short summary",
    "tags": ["parenting"],
    "header_image_id": 42,
    "body": [
      { "type": "h1", "value": "Heading" },
      { "type": "paragraph", "value": "<p>Body text</p>" }
    ]
  }'`;

  const curlRecipeImage = `curl -X POST ${base_url}recipe/api/v1/images/ \\
  -H "X-API-Key: YOUR_KEY" \\
  -F "type=banner" \\
  -F "file=@/path/to/banner.jpg"
# -> { "path": "recipe/images/ab12cd.jpg", "url": "https://.../ab12cd.jpg", "size": 84213 }`;

  const curlRecipeIcon = `curl -X POST ${base_url}recipe/api/v1/images/ \\
  -H "X-API-Key: YOUR_KEY" \\
  -F "type=category_icon" \\
  -F "file=@/path/to/category-icon.svg"
# Use type=audience_icon for audience icons.
# -> { "path": "recipe/icons/breakfast.svg", "url": "https://.../breakfast.svg", "size": 3210 }`;

  const curlRecipeIngredientImage = `curl -X POST ${base_url}recipe/api/v1/images/ \\
  -H "X-API-Key: YOUR_KEY" \\
  -F "type=ingredient_image" \\
  -F "file=@/path/to/cardamom.png"
# -> { "path": "Ingredients/images/cardamom.png", "url": "https://.../cardamom.png", "size": 4210 }`;

  const curlRecipeIngredientsList = `curl -H "X-API-Key: YOUR_KEY" \\\n  ${base_url}recipe/api/v1/ingredients/`;

  const curlRecipeIngredientCreate = `curl -X POST ${base_url}recipe/api/v1/ingredients/ \\
  -H "X-API-Key: YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Cardamom",
    "image_path": "Ingredients/images/cardamom.png",
    "indian_names": "Elaichi"
  }'`;

  const curlRecipeAdminResourceUpdate = `curl -H "X-API-Key: YOUR_KEY" \\
  ${base_url}recipe/api/v1/directions/?recipe=12

curl -X PATCH ${base_url}recipe/api/v1/directions/34/ \\
  -H "X-API-Key: YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "head_name": "Finish",
    "Directions": "<p>Bake until golden.</p>"
  }'`;

  const curlCompleteRecipeAdmin = `# List enabled home sections. Add ?search=<text> or supported field filters.
curl -H "X-API-Key: YOUR_KEY" \\
  "${base_url}recipe/api/v1/admin/home-sections/?enabled=true"

# Disable one section without replacing its remaining configuration.
curl -X PATCH ${base_url}recipe/api/v1/admin/home-sections/7/ \\
  -H "X-API-Key: YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{ "enabled": false }'`;

  const curlRecipeCategoryCreate = `curl -X POST ${base_url}recipe/api/v1/categories/ \\
  -H "X-API-Key: YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Breakfast",
    "index": 2,
    "tagline": "Morning meals",
    "icon_path": "recipe/icons/breakfast.svg",
    "parent_id": null
  }'`;

  const curlRecipeAudienceCreate = `curl -X POST ${base_url}recipe/api/v1/audiences/ \\
  -H "X-API-Key: YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Pregnancy",
    "index": 1,
    "tagline": "Pregnancy-safe recipes",
    "is_active": true,
    "icon_path": "audience_icons/pregnancy.svg"
  }'`;

  const curlRecipeCreate = `curl -X POST ${base_url}recipe/api/v1/recipes/ \\
  -H "X-API-Key: YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Banana Bread",
    "description": "A simple, moist loaf.",
    "difficulty_level": "Easy",
    "diet_type": "veg",
    "banner": "recipe/images/ab12cd.jpg",
    "user_id": 42,
    "category_ids": [3],
    "intended_audience_ids": [6],
    "ingredients": [
      {
        "ingredients_id": 7,
        "quantity": "2 cups",
        "help_text": "sifted"
      }
    ],
    "directions": [
      { "step": 1, "Directions": "<p>Mix everything.</p>" }
    ],
    "nutrition": { "kCal": 250, "protein": "4g" }
  }'`;

  const curlWorksheetFile = `curl -X POST ${base_url}core/worksheet/api/v1/files/ \\
  -H "X-API-Key: YOUR_KEY" \\
  -F "type=file" \\
  -F "file=@/path/to/sheet.pdf"
# -> { "path": "worksheet/files/ab12.pdf", "url": "https://.../ab12.pdf", "size": 84213 }`;

  const curlWorksheetIcon = `curl -X POST ${base_url}core/worksheet/api/v1/files/ \\
  -H "X-API-Key: YOUR_KEY" \\
  -F "type=category_icon" \\
  -F "file=@/path/to/category-icon.svg"
# Use type=audience_icon for audience icons.
# -> { "path": "worksheet_icons/fine-motor.svg", "url": "https://.../fine-motor.svg", "size": 2842 }`;

  const curlWorksheetCategoryCreate = `curl -X POST ${base_url}core/worksheet/api/v1/categories/ \\
  -H "X-API-Key: YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Fine Motor",
    "index": 2,
    "status": true,
    "icon_path": "worksheet_icons/fine-motor.svg"
  }'`;

  const curlWorksheetAudienceCreate = `curl -X POST ${base_url}core/worksheet/api/v1/audiences/ \\
  -H "X-API-Key: YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Toddlers",
    "index": 3,
    "tagline": "For toddler activities",
    "is_active": true,
    "icon_path": "audience_icons/toddlers.svg"
  }'`;

  const curlWorksheetGeneration = `curl -X POST ${base_url}core/worksheet/api/v1/generations/ \\
  -H "X-API-Key: YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Tracing Circles",
    "prompt": "A clean printable worksheet with large circles for preschool tracing practice.",
    "description": "Practice hand control",
    "age": 4,
    "category_ids": [1],
    "audience_ids": [4]
  }'
# Creates a Worksheet and returns generation instrumentation. Model/settings are controlled in Django admin.`;

  const curlWorksheetCreate = `curl -X POST ${base_url}core/worksheet/api/v1/worksheets/ \\
  -H "X-API-Key: YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "Counting 1-10",
    "description": "A simple counting sheet.",
    "age": 4,
    "worksheet_file": "worksheet/files/ab12.pdf",
    "thumbnail": "worksheet/thumbnails/cd34.jpg",
    "category_ids": [1, 2],
    "audience_ids": [4]
  }'`;

  const curlCommunityQuestion = `curl -X POST ${base_url}community/api/v1/questions/ \\
  -H "X-API-Key: YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "How do I get my newborn to sleep?",
    "description": "<p>Any tips welcome.</p>",
    "community_id": 3,
    "user_id": 42,
    "tag_id": 7
  }'`;

  const curlCommunityReply = `curl -X POST ${base_url}community/api/v1/answers/ \\
  -H "X-API-Key: YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "title": "<p>Swaddling helped us.</p>",
    "question_id": 12,
    "user_id": 42
  }'`;

  const scopeCopy =
    Object.entries(scopeCatalogue)
      .map(([scope, desc]) => `${scope}: ${desc}`)
      .join("\n") || "Scope list unavailable. Refresh after the backend responds.";

  const buildSectionCopy = (title, usage, rows, snippets) =>
    [
      title,
      usage,
      "Endpoints:",
      formatEndpointLines(rows),
      "Examples:",
      snippets.join("\n\n"),
    ].join("\n\n");

  const authSectionCopy = [
    "MyBurgo API key usage",
    `Base URL: ${base_url}`,
    "Send X-API-Key: YOUR_KEY on every API-key request.",
    "Grant only the scopes required by the integration.",
    "Scopes:",
    scopeCopy,
  ].join("\n\n");

  const blogSectionCopy = buildSectionCopy(
    "Blog API",
    "Upload images first, then create posts that reference returned image ids.",
    blogEndpoints,
    [curlImage, curlList, curlCreate]
  );

  const recipeSectionCopy = buildSectionCopy(
    "Recipe API",
    "Use recipe:* for backward-compatible authoring. Use the separate recipe-admin:* scopes only for trusted integrations that need the complete recipe administration surface, including user-linked activity and AI configuration. Pass user_id when an integration needs to attribute the upload to a specific app user; omit it to use the API-key user.",
    [...recipeEndpoints, ...recipeAdminEndpoints],
    [
      curlRecipeImage,
      curlRecipeIcon,
      curlRecipeIngredientImage,
      curlRecipeCategoryCreate,
      curlRecipeAudienceCreate,
      curlRecipeIngredientsList,
      curlRecipeIngredientCreate,
      curlRecipeAdminResourceUpdate,
      curlCompleteRecipeAdmin,
      curlRecipeCreate,
    ]
  );

  const worksheetSectionCopy = buildSectionCopy(
    "Worksheet API",
    "Use files and worksheets when you already have a worksheet asset. Use generations when MyBurgo should create a new printable worksheet image from a prompt using the admin-controlled GPT image configuration.",
    worksheetEndpoints,
    [
      curlWorksheetFile,
      curlWorksheetIcon,
      curlWorksheetCategoryCreate,
      curlWorksheetAudienceCreate,
      curlWorksheetGeneration,
      curlWorksheetCreate,
    ]
  );

  const communitySectionCopy = buildSectionCopy(
    "Community API",
    "Community writes default to the API-key user. Include user_id to post or reply as a specific app user.",
    communityEndpoints,
    [curlCommunityQuestion, curlCommunityReply]
  );

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.mainContainer}>
        <div className={styles.settingsPage}>
          {showAlert &&
            alerts.map((alert) => (
              <CustomAlert
                key={alert.id}
                title={alert.title}
                message={alert.message}
                onClose={() => handleRemoveAlert(alert.id)}
                autoClose={alert.autoClose}
                type={alert.type}
              />
            ))}

          {/* Header */}
          <div className={styles.pageHeader}>
            <div>
              <h3 className={styles.pageTitle}>API Keys</h3>
              <p className={styles.description}>
                Create and manage keys that grant programmatic access to MyBurgo
                (the blog, recipe, worksheet and community read/write APIs). Keys
                are shown only once at creation — store them securely.
              </p>
            </div>
            <Button
              text="+ Create API Key"
              onClick={openCreateModal}
              style={{ fontSize: "14px", padding: "10px 18px", whiteSpace: "nowrap" }}
            />
          </div>

          {/* Keys table */}
          <div className={styles.settingsSection}>
            <div className={styles.card}>
              {keys.length === 0 ? (
                <div className={styles.emptyState}>
                  No API keys yet. Click <strong>Create API Key</strong> to issue
                  your first one.
                </div>
              ) : (
                <div className={styles.tableWrapper}>
                  <table className={styles.keysTable}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Key prefix</th>
                        <th>Scopes</th>
                        <th>Status</th>
                        <th>Last used</th>
                        <th>Expires</th>
                        <th className={styles.actionsHead}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {keys.map((key) => {
                        const status = statusOf(key);
                        return (
                          <tr key={key.id}>
                            <td>{key.name}</td>
                            <td>
                              <code className={styles.prefix}>{key.prefix}…</code>
                            </td>
                            <td>
                              <div className={styles.scopeChips}>
                                {(key.scopes || []).map((s) => (
                                  <span key={s} className={styles.scopeChip}>
                                    {s}
                                  </span>
                                ))}
                              </div>
                            </td>
                            <td>
                              <span className={`${styles.badge} ${status.cls}`}>
                                {status.label}
                              </span>
                            </td>
                            <td>{formatDate(key.last_used_at)}</td>
                            <td>{formatDate(key.expires_at)}</td>
                            <td>
                              <div className={styles.rowActions}>
                                {!key.is_revoked && (
                                  <>
                                    <button
                                      className={styles.linkBtn}
                                      onClick={() => rotateKey(key)}
                                    >
                                      Rotate
                                    </button>
                                    <button
                                      className={`${styles.linkBtn} ${styles.warn}`}
                                      onClick={() => revokeKey(key)}
                                    >
                                      Revoke
                                    </button>
                                  </>
                                )}
                                <button
                                  className={`${styles.linkBtn} ${styles.danger}`}
                                  onClick={() => deleteKey(key)}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Documentation */}
          <div className={styles.settingsSection}>
            <div className={styles.docsHeader}>
              <div>
                <h3>Integration guide</h3>
                <p className={styles.description}>
                  Use this as the dashboard-side runbook for API-key
                  integrations. Each section can be copied in one click for
                  handoff to an integration partner.
                </p>
              </div>
              <CopyButton
                label="Copy auth"
                onClick={() => copyToClipboard(authSectionCopy, "Auth copied")}
                variant="section"
              />
            </div>

            <div className={styles.docsShell}>
              <aside className={styles.docsNav} aria-label="API guide sections">
                <a href="#api-auth">Auth and scopes</a>
                <a href="#blog-api">Blog</a>
                <a href="#recipe-api">Recipes</a>
                <a href="#worksheet-api">Worksheets</a>
                <a href="#community-api">Community</a>
              </aside>

              <div className={styles.docsContent}>
                <section className={`${styles.docPanel} ${styles.docIntro}`} id="api-auth">
                  <div className={styles.docSectionHeader}>
                    <div>
                      <span className={styles.docEyebrow}>Start here</span>
                      <h4 className={styles.docHeading}>Authentication and scope setup</h4>
                      <p className={styles.description}>
                        Send <code>X-API-Key: YOUR_KEY</code> on API-key
                        requests. Keys act as the user they were issued for, so
                        only grant the scopes required by the integration.
                      </p>
                    </div>
                    <CopyButton
                      label="Copy section"
                      onClick={() => copyToClipboard(authSectionCopy, "Auth copied")}
                      variant="section"
                    />
                  </div>

                  <div className={styles.workflowGrid}>
                    <div className={styles.workflowStep}>
                      <strong>1. Pick scopes</strong>
                      <span>Choose the read/write/delete scopes the partner needs.</span>
                    </div>
                    <div className={styles.workflowStep}>
                      <strong>2. Upload assets first</strong>
                      <span>Images and files return ids or paths used in create payloads.</span>
                    </div>
                    <div className={styles.workflowStep}>
                      <strong>3. Create records</strong>
                      <span>Pass relation ids after categories, audiences, and master rows exist.</span>
                    </div>
                  </div>

                  <h5 className={styles.subHeading}>Available scopes</h5>
                  <div className={styles.scopeGrid}>
                    {Object.entries(scopeCatalogue).map(([scope, desc]) => (
                      <div className={styles.scopeTile} key={scope}>
                        <code>{scope}</code>
                        <span>{desc}</span>
                      </div>
                    ))}
                    {Object.keys(scopeCatalogue).length === 0 && (
                      <p className={styles.description}>
                        Scope list unavailable. Refresh after the backend responds.
                      </p>
                    )}
                  </div>
                </section>

                <DocSection
                  id="blog-api"
                  eyebrow="Content"
                  title="Blog API"
                  description="Upload cover or body images first, then create posts that reference the returned image ids."
                  copyText={blogSectionCopy}
                  onCopy={copyToClipboard}
                >
                  <EndpointTable rows={blogEndpoints} />
                  <div className={styles.exampleGrid}>
                    <div className={styles.exampleBlock}>
                      <h5 className={styles.subHeading}>Upload an image</h5>
                      <p className={styles.description}>
                        Use the returned <code>id</code> as{" "}
                        <code>header_image_id</code> or inside image body blocks.
                      </p>
                      <CodeBlock value={curlImage} label="Copy" onCopy={copyToClipboard} />
                    </div>
                    <div className={styles.exampleBlock}>
                      <h5 className={styles.subHeading}>List posts</h5>
                      <CodeBlock value={curlList} label="Copy" onCopy={copyToClipboard} />
                    </div>
                    <div className={styles.exampleBlockWide}>
                      <h5 className={styles.subHeading}>Create a post</h5>
                      <p className={styles.description}>
                        <code>body</code> is Wagtail StreamField JSON with
                        blocks such as <code>h1</code>, <code>paragraph</code>,
                        <code>image_carousel</code>, <code>video</code>, and
                        <code>custom_embed</code>.
                      </p>
                      <CodeBlock value={curlCreate} label="Copy" onCopy={copyToClipboard} />
                    </div>
                  </div>
                </DocSection>

                <DocSection
                  id="recipe-api"
                  eyebrow="Recipes"
                  title="Recipe API"
                  description="Use recipe authoring scopes for normal integrations, or dedicated recipe administration scopes for trusted tools that must manage every recipe model."
                  copyText={recipeSectionCopy}
                  onCopy={copyToClipboard}
                >
                  <div className={styles.callout}>
                    <strong>Uploader attribution</strong>
                    <span>
                      Pass <code>user_id</code> when the recipe should show a
                      specific uploader. Omit it to attribute the recipe to the
                      API-key user.
                    </span>
                  </div>
                  <EndpointTable rows={recipeEndpoints} />
                  <div className={styles.callout}>
                    <strong>Complete administration uses separate scopes</strong>
                    <span>
                      Grant <code>recipe-admin:read</code>,{" "}
                      <code>recipe-admin:write</code>, or{" "}
                      <code>recipe-admin:delete</code> only to trusted
                      back-office integrations. This surface includes
                      user-linked engagement, cook sessions, meal plans, home
                      curation, and AI configuration. Existing{" "}
                      <code>recipe:*</code> keys do not gain that access.
                    </span>
                  </div>
                  <h5 className={styles.subHeading}>
                    Complete recipe administration endpoints
                  </h5>
                  <p className={styles.description}>
                    Collection paths support list and create. Append{" "}
                    <code>{"{id}/"}</code> to retrieve, update, or delete. Use{" "}
                    <code>GET</code> with the read scope, <code>POST</code>,{" "}
                    <code>PUT</code>, or <code>PATCH</code> with the write scope,
                    and <code>DELETE</code> with the delete scope.
                  </p>
                  <EndpointTable rows={recipeAdminEndpoints} />
                  <div className={styles.exampleGrid}>
                    <div className={styles.exampleBlock}>
                      <h5 className={styles.subHeading}>Upload a banner</h5>
                      <p className={styles.description}>
                        Use <code>type=banner</code> and pass the returned{" "}
                        <code>path</code> as <code>banner</code>.
                      </p>
                      <CodeBlock value={curlRecipeImage} label="Copy" onCopy={copyToClipboard} />
                    </div>
                    <div className={styles.exampleBlock}>
                      <h5 className={styles.subHeading}>Upload relation icons</h5>
                      <p className={styles.description}>
                        Use <code>type=category_icon</code> or{" "}
                        <code>type=audience_icon</code>, then pass{" "}
                        <code>icon_path</code>.
                      </p>
                      <CodeBlock value={curlRecipeIcon} label="Copy" onCopy={copyToClipboard} />
                    </div>
                    <div className={styles.exampleBlock}>
                      <h5 className={styles.subHeading}>Upload ingredient images</h5>
                      <p className={styles.description}>
                        Use <code>type=ingredient_image</code>, then pass the
                        returned <code>path</code> as <code>image_path</code> on
                        the ingredient master.
                      </p>
                      <CodeBlock
                        value={curlRecipeIngredientImage}
                        label="Copy"
                        onCopy={copyToClipboard}
                      />
                    </div>
                    <div className={styles.exampleBlock}>
                      <h5 className={styles.subHeading}>Create a category</h5>
                      <CodeBlock
                        value={curlRecipeCategoryCreate}
                        label="Copy"
                        onCopy={copyToClipboard}
                      />
                    </div>
                    <div className={styles.exampleBlock}>
                      <h5 className={styles.subHeading}>Create an audience</h5>
                      <CodeBlock
                        value={curlRecipeAudienceCreate}
                        label="Copy"
                        onCopy={copyToClipboard}
                      />
                    </div>
                    <div className={styles.exampleBlock}>
                      <h5 className={styles.subHeading}>List ingredient master</h5>
                      <p className={styles.description}>
                        Use this before recipe creation to find the{" "}
                        <code>ingredients_id</code> values.
                      </p>
                      <CodeBlock
                        value={curlRecipeIngredientsList}
                        label="Copy"
                        onCopy={copyToClipboard}
                      />
                    </div>
                    <div className={styles.exampleBlock}>
                      <h5 className={styles.subHeading}>Create ingredient master</h5>
                      <p className={styles.description}>
                        Ingredient masters can include <code>image_path</code> or
                        accept multipart <code>image</code> directly.
                      </p>
                      <CodeBlock
                        value={curlRecipeIngredientCreate}
                        label="Copy"
                        onCopy={copyToClipboard}
                      />
                    </div>
                    <div className={styles.exampleBlockWide}>
                      <h5 className={styles.subHeading}>Update authoring child resources</h5>
                      <p className={styles.description}>
                        The recipe admin resources support <code>GET</code>,{" "}
                        <code>PUT</code>, and <code>PATCH</code>. Use these for
                        directions, child ingredient rows, nutrition, views,
                        favourites, likes, and filter chips.
                      </p>
                      <CodeBlock
                        value={curlRecipeAdminResourceUpdate}
                        label="Copy"
                        onCopy={copyToClipboard}
                      />
                    </div>
                    <div className={styles.exampleBlockWide}>
                      <h5 className={styles.subHeading}>Manage every recipe section</h5>
                      <p className={styles.description}>
                        List routes accept <code>?search=</code> where the
                        matching dashboard section is searchable, plus its
                        field filters such as <code>?enabled=false</code>,{" "}
                        <code>?recipe=12</code>, <code>?user=42</code>, or{" "}
                        <code>?status=active</code>. Partial updates preserve
                        fields you do not send.
                      </p>
                      <CodeBlock
                        value={curlCompleteRecipeAdmin}
                        label="Copy"
                        onCopy={copyToClipboard}
                      />
                    </div>
                    <div className={styles.exampleBlockWide}>
                      <h5 className={styles.subHeading}>Create a recipe</h5>
                      <p className={styles.description}>
                        <code>difficulty_level</code> accepts <code>Easy</code>,
                        <code>Medium</code>, or <code>Hard</code>. Relation lists
                        such as <code>category_ids</code> and{" "}
                        <code>intended_audience_ids</code> replace the current
                        relation set on update.
                      </p>
                      <CodeBlock value={curlRecipeCreate} label="Copy" onCopy={copyToClipboard} />
                    </div>
                  </div>
                </DocSection>

                <DocSection
                  id="worksheet-api"
                  eyebrow="Worksheets"
                  title="Worksheet API"
                  description="Use manual worksheet creation when an asset already exists. Use AI generation when MyBurgo should create a new printable worksheet image from a prompt."
                  copyText={worksheetSectionCopy}
                  onCopy={copyToClipboard}
                >
                  <div className={styles.callout}>
                    <strong>AI generation control</strong>
                    <span>
                      <code>/generations/</code> uses the model, prompt
                      template, quality, size, moderation, and daily limits
                      configured in Django admin. Requests are instrumented in
                      the backend generation log.
                    </span>
                  </div>
                  <EndpointTable rows={worksheetEndpoints} />
                  <div className={styles.exampleGrid}>
                    <div className={styles.exampleBlock}>
                      <h5 className={styles.subHeading}>Upload a worksheet file</h5>
                      <p className={styles.description}>
                        Use <code>type=file</code> for PDFs or images, and{" "}
                        <code>type=thumbnail</code> for thumbnails.
                      </p>
                      <CodeBlock value={curlWorksheetFile} label="Copy" onCopy={copyToClipboard} />
                    </div>
                    <div className={styles.exampleBlock}>
                      <h5 className={styles.subHeading}>Upload relation icons</h5>
                      <p className={styles.description}>
                        Upload category or audience icons before creating
                        relation records with <code>icon_path</code>.
                      </p>
                      <CodeBlock value={curlWorksheetIcon} label="Copy" onCopy={copyToClipboard} />
                    </div>
                    <div className={styles.exampleBlock}>
                      <h5 className={styles.subHeading}>Create a category</h5>
                      <CodeBlock
                        value={curlWorksheetCategoryCreate}
                        label="Copy"
                        onCopy={copyToClipboard}
                      />
                    </div>
                    <div className={styles.exampleBlock}>
                      <h5 className={styles.subHeading}>Create an audience</h5>
                      <CodeBlock
                        value={curlWorksheetAudienceCreate}
                        label="Copy"
                        onCopy={copyToClipboard}
                      />
                    </div>
                    <div className={styles.exampleBlockWide}>
                      <h5 className={styles.subHeading}>Generate a worksheet</h5>
                      <p className={styles.description}>
                        This creates a worksheet file and a normal worksheet
                        record. Use this only when the requested output should
                        be created from a prompt.
                      </p>
                      <CodeBlock
                        value={curlWorksheetGeneration}
                        label="Copy"
                        onCopy={copyToClipboard}
                      />
                    </div>
                    <div className={styles.exampleBlockWide}>
                      <h5 className={styles.subHeading}>Create from an uploaded file</h5>
                      <p className={styles.description}>
                        Use this when a partner already provides the finished
                        worksheet asset and optional thumbnail.
                      </p>
                      <CodeBlock value={curlWorksheetCreate} label="Copy" onCopy={copyToClipboard} />
                    </div>
                  </div>
                </DocSection>

                <DocSection
                  id="community-api"
                  eyebrow="Community"
                  title="Community API"
                  description="Post and read community questions and replies. Include user_id when the visible author should be a specific app user."
                  copyText={communitySectionCopy}
                  onCopy={copyToClipboard}
                >
                  <EndpointTable rows={communityEndpoints} />
                  <div className={styles.exampleGrid}>
                    <div className={styles.exampleBlock}>
                      <h5 className={styles.subHeading}>Post a question</h5>
                      <CodeBlock
                        value={curlCommunityQuestion}
                        label="Copy"
                        onCopy={copyToClipboard}
                      />
                    </div>
                    <div className={styles.exampleBlock}>
                      <h5 className={styles.subHeading}>Post a reply</h5>
                      <CodeBlock
                        value={curlCommunityReply}
                        label="Copy"
                        onCopy={copyToClipboard}
                      />
                    </div>
                  </div>
                  <p className={`${styles.description} ${styles.note}`}>
                    These endpoints live on the MyBurgo backend. If a brand-new
                    key returns 404, the backend API-key release may not be
                    deployed to this environment yet.
                  </p>
                </DocSection>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create modal */}
      {showCreateModal && (
        <Modal title="Create API Key" onClose={() => setShowCreateModal(false)}>
          <div className={styles.form}>
            <label className={styles.formLabel}>Name</label>
            <input
              className={styles.textInput}
              type="text"
              placeholder="e.g. Marketing CMS integration"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />

            <label className={styles.formLabel}>Permissions</label>
            <div className={styles.scopePicker}>
              {Object.entries(scopeCatalogue).map(([scope, desc]) => (
                <label key={scope} className={styles.scopeOption}>
                  <input
                    type="checkbox"
                    checked={form.scopes.includes(scope)}
                    onChange={() => toggleScope(scope)}
                  />
                  <span>
                    <code>{scope}</code>
                    <span className={styles.scopeDesc}> — {desc}</span>
                  </span>
                </label>
              ))}
              {Object.keys(scopeCatalogue).length === 0 && (
                <p className={styles.description}>
                  No scopes available. Ensure the backend is reachable.
                </p>
              )}
            </div>

            <div className={styles.formRow}>
              <div className={styles.formCol}>
                <label className={styles.formLabel}>
                  Expires on <span className={styles.optional}>(optional)</span>
                </label>
                <input
                  className={styles.textInput}
                  type="date"
                  value={form.expires_at}
                  onChange={(e) =>
                    setForm({ ...form, expires_at: e.target.value })
                  }
                />
              </div>
              <div className={styles.formCol}>
                <label className={styles.formLabel}>
                  Rate limit <span className={styles.optional}>(optional)</span>
                </label>
                <input
                  className={styles.textInput}
                  type="text"
                  placeholder="e.g. 1000/day"
                  value={form.throttle_rate}
                  onChange={(e) =>
                    setForm({ ...form, throttle_rate: e.target.value })
                  }
                />
              </div>
            </div>

            <p className={styles.description}>
              The key will act as your account. Blog posts created with it are
              attributed to you. Recipe upload, community question, and community
              reply payloads can pass <code>user_id</code> when the integration
              should attribute content to a specific app user.
            </p>

            <div className={styles.modalActions}>
              <Button
                text="Cancel"
                backgroundColor="#ccc"
                onClick={() => setShowCreateModal(false)}
              />
              <Button text="Create Key" onClick={createKey} />
            </div>
          </div>
        </Modal>
      )}

      {/* One-time secret reveal */}
      {newSecret && (
        <Modal title="Copy your API key now" onClose={() => setNewSecret(null)}>
          <div className={styles.form}>
            <p className={`${styles.description} ${styles.secretWarning}`}>
              This is the only time the full key for{" "}
              <strong>{newSecret.name}</strong> will be shown. Store it securely —
              it cannot be retrieved again, only rotated.
            </p>
            <div className={styles.secretBoxWrap}>
              <code className={styles.secretBox}>{newSecret.key}</code>
            </div>
            <div className={styles.modalActions}>
              <Button
                text="Copy key"
                onClick={() => copyToClipboard(newSecret.key, "Key copied")}
              />
              <Button
                text="Done"
                backgroundColor="#ccc"
                onClick={() => setNewSecret(null)}
              />
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default ApiKeys;
